import React from 'react';
import { api } from '../api.js';
import * as T from '../time.js';
import { Empty, Loading, PH, Panel } from './components.jsx';
import { Button, Checkbox, Field, Pill } from './ds/fgcp-ds.jsx';
import { applyServerData } from './data.jsx';

/**
 * 업무 정책(계획 문서 5.4 4c) — 그 분야(prefix: 'leave.' 또는 'approval.')의 회사 규정 값. 바꿀 수 있는지·검사는 서버가 정한다(actions.edit, 400).
 * 휴가 관리 탭과 결재 양식 관리가 각자 자기 분야를 보인다(맡는 관리자가 일하는 화면)
 */
const DAY_LABEL = ['월', '화', '수', '목', '금', '토', '일'];

function Editor({ item, onSave, onReset }) {
  const [v, setV] = React.useState(item.value);
  const changed = JSON.stringify(v) !== JSON.stringify(item.value);
  let input;
  if (item.key === 'leave.workDay') {
    input = <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 8 }}>
      {[['start', '시작'], ['halfSplit', '반차 경계'], ['end', '끝']].map(([k, l]) => (
        <Field key={k} label={l} type="time" value={v[k]} disabled={!item.actions.edit} onChange={e => setV({ ...v, [k]: e.target.value })} />))}
    </div>;
  } else if (item.key === 'leave.daysOff') {
    input = <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
      {DAY_LABEL.map((l, i) => (
        <Checkbox key={l} size="sm" label={l} aria-label={`쉬는 요일 ${l}`} disabled={!item.actions.edit} checked={v.includes(i + 1)}
          onChange={on => setV(on ? [...v, i + 1].sort() : v.filter(d => d !== i + 1))} />))}
    </div>;
  } else if (typeof item.default === 'boolean') {
    input = <Checkbox label="허용" aria-label={item.label} disabled={!item.actions.edit} checked={v} onChange={setV} />;
  } else {
    input = <Field value={v} disabled={!item.actions.edit} aria-label={item.label} onChange={e => setV(e.target.value.toUpperCase())} />;
  }
  return (
    <div data-policy={item.key} style={{ padding: '12px 16px', borderTop: '1px solid var(--line)', display: 'flex', flexDirection: 'column', gap: 8 }}>
      <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
        <span style={{ fontSize: 12.5, fontWeight: 600 }}>{item.label}</span>
        {item.isDefault ? <Pill tone="neutral" dot={false}>기본값</Pill> : <Pill tone="info" dot={false}>바꿈 · {item.updatedByName ?? ''} {item.updatedAt ? T.dateTime(item.updatedAt) : ''}</Pill>}
      </div>
      {input}
      {item.actions.edit && <div style={{ display: 'flex', gap: 6 }}>
        <Button size="sm" disabled={!changed} onClick={() => onSave(v)}>정책 저장</Button>
        {!item.isDefault && <Button size="sm" variant="secondary" onClick={onReset}>기본값으로</Button>}
      </div>}
    </div>
  );
}

export function PolicyPanel({ prefix, title, onToast }) {
  const [items, setItems] = React.useState(null);
  const load = React.useCallback(() => api.get('/api/v1/policy/settings').then(r => setItems(r.items.filter(i => i.key.startsWith(prefix)))).catch(e => onToast(e.message)), [prefix, onToast]);
  React.useEffect(() => { void load(); }, [load]);
  // 저장하면 화면 계산에 쓰는 값(휴무 요일 등)도 다시 받는다
  const reloadPublic = () => api.get('/api/v1/policy').then(p => applyServerData({ policy: p })).catch(e => onToast('정책 값을 다시 받지 못했습니다 — ' + e.message));
  const save = (key, value) => api.put(`/api/v1/policy/settings/${encodeURIComponent(key)}`, { value })
    .then(() => { onToast(value === null ? '기본값으로 되돌렸습니다' : '업무 정책을 저장했습니다'); return Promise.all([load(), reloadPublic()]); })
    .catch(e => onToast(e.message));
  return (
    <Panel>
      <PH title={title} sub="회사 규정 값 — 바꾼 값은 새 신청·새 문서부터" />
      {!items ? <Loading title="불러오는 중" /> : items.map(i => (
        <Editor key={i.key + JSON.stringify(i.value)} item={i} onSave={v => save(i.key, v)} onReset={() => save(i.key, null)} />))}
    </Panel>
  );
}
